import PublicHeader from '@/components/layout/PublicHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { usePageSEO } from '@/hooks/usePageSEO';
import { useTranslation } from 'react-i18next';
import { TEAM_MEMBERS } from '@/data/team';

export default function AboutPage() {
  const { t } = useTranslation();

  usePageSEO('about', {
    canonicalPath: '/about',
    breadcrumbs: [{ name: 'About' }],
    // Only adds real Person entities when TEAM_MEMBERS is actually
    // populated — an empty array here would be worse than no jsonLd at
    // all (a mainEntity claiming zero people isn't a meaningful signal).
    jsonLd: TEAM_MEMBERS.length > 0 ? {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'PlanWise',
        employee: TEAM_MEMBERS.map((member) => ({
          '@type': 'Person',
          name: member.name,
          jobTitle: member.role,
          description: member.bio,
          ...(member.linkedinUrl ? { sameAs: [member.linkedinUrl] } : {}),
        })),
      },
    } : undefined,
  });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 font-sans">
      <PublicHeader />

      <div className="pt-28" />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
          {t('about.heading')}
        </h1>

        <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 space-y-6">
          <p>{t('about.intro')}</p>
          <p>{t('about.whatWeDo')}</p>
          <p>{t('about.whoItsFor')}</p>

          {TEAM_MEMBERS.length > 0 && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white pt-4">
                {t('about.teamHeading')}
              </h2>
              <div className="grid gap-6 sm:grid-cols-2 not-prose">
                {TEAM_MEMBERS.map((member) => (
                  <div key={member.name} className="flex gap-4 items-start">
                    {member.photoUrl && (
                      <img
                        src={member.photoUrl}
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {member.linkedinUrl ? (
                          <a href={member.linkedinUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {member.name}
                          </a>
                        ) : member.name}
                      </p>
                      <p className="text-sm text-primary-600 dark:text-primary-400">{member.role}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{member.bio}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white pt-4">
            {t('about.getInTouchHeading')}
          </h2>
          <p>
            {t('about.getInTouchBody')}{' '}
            <a href="mailto:support@planwise.social" className="text-primary-600 dark:text-primary-400 hover:underline">
              support@planwise.social
            </a>
            .
          </p>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
